import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"
import { update_status_task_path, reorder_kanban_column_tasks_path, reset_kanban_column_tasks_path } from "../routes"

export default class extends Controller {
  static values = {
    csrfToken: String
  }

  static targets = ["card", "cardsContainer", "column", "emptyPlaceholder"]

  // ─── State ────────────────────────────────────────────────────────────────

  connect() {
    this.draggedCard = null
    this.draggedTaskId = null
    this.sourceContainer = null
    this.dragPlaceholder = null
    // Touch drag state
    this.touchCard = null
    this.touchClone = null
    this.touchTargetColumn = null
    this.touchDragStarted = false
    this.touchHoldTimer = null
    this.touchStartX = 0
    this.touchStartY = 0
  }

  disconnect() {
    this._cancelTouchDrag()
    this.dragPlaceholder?.remove()
    this.dragPlaceholder = null
  }

  // ─── HTML5 Drag & Drop (desktop) ──────────────────────────────────────────

  dragStart(event) {
    const card = event.currentTarget
    this.draggedCard = card
    this.draggedTaskId = card.dataset.taskId
    this.sourceContainer = card.closest('[data-task-kanban-target="cardsContainer"]')

    event.dataTransfer.effectAllowed = 'move'
    event.dataTransfer.setData('text/plain', this.draggedTaskId)

    // Explicit ghost image: the browser uses the full card at natural size
    event.dataTransfer.setDragImage(card, card.offsetWidth / 2, 30)

    // Placeholder travels with the cursor
    this.dragPlaceholder = this._createDropPlaceholder(card.offsetHeight)

    // Delay class addition so the ghost snapshot is taken before we fade the card
    requestAnimationFrame(() => {
      card.classList.add('kanban-card--dragging')
    })
  }

  dragEnd(event) {
    event.currentTarget.classList.remove('kanban-card--dragging')
    this.dragPlaceholder?.remove()
    this.dragPlaceholder = null
    this._clearAllDragOver()
    this.draggedCard = null
    this.draggedTaskId = null
    this.sourceContainer = null
  }

  // dragOver fires continuously — use it to reposition the placeholder precisely
  dragOver(event) {
    const column = event.currentTarget
    if (column.dataset.dropDisabled) return

    event.preventDefault()
    event.dataTransfer.dropEffect = 'move'

    if (!this.dragPlaceholder) return

    const container = column.querySelector('[data-task-kanban-target="cardsContainer"]')
    if (container) this._insertPlaceholderAt(container, event.clientY)
  }

  dragEnter(event) {
    const column = event.currentTarget
    if (column.dataset.dropDisabled) return

    event.preventDefault()
    column.classList.add('kanban-column--drag-over')

    if (this.dragPlaceholder) {
      const container = column.querySelector('[data-task-kanban-target="cardsContainer"]')
      if (container) this._insertPlaceholderAt(container, event.clientY)
    }
  }

  dragLeave(event) {
    const column = event.currentTarget
    if (column.contains(event.relatedTarget)) return
    column.classList.remove('kanban-column--drag-over')
    this.dragPlaceholder?.remove()
  }

  drop(event) {
    event.preventDefault()
    const column = event.currentTarget
    column.classList.remove('kanban-column--drag-over')

    if (column.dataset.dropDisabled) { this.dragPlaceholder?.remove(); return }

    const newStatus = column.dataset.status
    const taskId = this.draggedTaskId || event.dataTransfer.getData('text/plain')
    if (!taskId || !newStatus) return

    const card = this.draggedCard || this.element.querySelector(`[data-task-id="${taskId}"]`)
    if (!card) return

    const oldStatus = this.sourceContainer?.dataset?.status
    const targetContainer = column.querySelector('[data-task-kanban-target="cardsContainer"]')
    if (!targetContainer) { this.dragPlaceholder?.remove(); return }

    if (oldStatus === newStatus) {
      // Same column: reorder only (no status update needed)
      this._reorderCardAtPlaceholder(card, targetContainer, newStatus)
      window.phCapture?.('kanban_card_dragged', { feature: 'tasks', action: 'dragged', kind: 'reorder', status: newStatus })
    } else {
      // Different column: move card + update status
      this._moveCardAtPlaceholder(card, targetContainer, newStatus, oldStatus)
      window.phCapture?.('kanban_card_dragged', { feature: 'tasks', action: 'dragged', kind: 'move', from_status: oldStatus, to_status: newStatus })
    }
  }

  // ─── Touch Drag (tablet/mobile) ───────────────────────────────────────────
  // Drag starts only after a 220ms hold to allow vertical scroll on touch devices.

  touchStart(event) {
    const card = event.currentTarget
    this.touchCard = card
    this.draggedTaskId = card.dataset.taskId
    this.sourceContainer = card.closest('[data-task-kanban-target="cardsContainer"]')
    this.touchDragStarted = false

    const touch = event.touches[0]
    this.touchStartX = touch.clientX
    this.touchStartY = touch.clientY

    // Start hold timer — drag activates after 220ms of staying still
    this.touchHoldTimer = setTimeout(() => {
      if (this.touchCard) this._startTouchDrag(touch)
    }, 220)
  }

  touchMove(event) {
    const touch = event.touches[0]

    if (!this.touchDragStarted) {
      // If user moved significantly before hold timer fired → it's a scroll, cancel drag
      const dx = Math.abs(touch.clientX - this.touchStartX)
      const dy = Math.abs(touch.clientY - this.touchStartY)
      if (dx > 8 || dy > 8) this._cancelTouchDrag()
      return // don't preventDefault — let the column scroll naturally
    }

    // Drag is active: prevent default scroll and update clone/placeholder
    event.preventDefault()
    this._positionTouchClone(touch)

    this.touchClone.style.display = 'none'
    const elementBelow = document.elementFromPoint(touch.clientX, touch.clientY)
    this.touchClone.style.display = ''

    const column = elementBelow?.closest('[data-task-kanban-target="column"]')

    if (column && column !== this.touchTargetColumn) {
      if (this.touchTargetColumn) this.touchTargetColumn.classList.remove('kanban-column--drag-over')

      this.touchTargetColumn = column
      if (!column.dataset.dropDisabled) {
        column.classList.add('kanban-column--drag-over')
        const container = column.querySelector('[data-task-kanban-target="cardsContainer"]')
        if (container && this.dragPlaceholder) this._insertPlaceholderAt(container, touch.clientY)
      }
    } else if (column === this.touchTargetColumn && !column.dataset.dropDisabled) {
      const container = column.querySelector('[data-task-kanban-target="cardsContainer"]')
      if (container && this.dragPlaceholder) this._insertPlaceholderAt(container, touch.clientY)
    } else if (!column && this.touchTargetColumn) {
      this.touchTargetColumn.classList.remove('kanban-column--drag-over')
      this.dragPlaceholder?.remove()
      this.touchTargetColumn = null
    }
  }

  touchEnd(event) {
    clearTimeout(this.touchHoldTimer)
    this.touchHoldTimer = null

    if (!this.touchDragStarted) {
      // Hold never fired — it was a tap or scroll, clean up quietly
      this._cancelTouchDrag()
      return
    }

    this._removeTouchClone()
    const card = this.touchCard
    if (!card) return
    card.classList.remove('kanban-card--dragging')

    const targetColumn = this.touchTargetColumn

    if (targetColumn) {
      targetColumn.classList.remove('kanban-column--drag-over')
      const newStatus = targetColumn.dataset.status
      const oldStatus = this.sourceContainer?.dataset?.status
      const targetContainer = targetColumn.querySelector('[data-task-kanban-target="cardsContainer"]')

      if (newStatus && targetContainer && !targetColumn.dataset.dropDisabled) {
        if (newStatus === oldStatus) {
          // Same column: reorder only
          this._reorderCardAtPlaceholder(card, targetContainer, newStatus)
          window.phCapture?.('kanban_card_dragged', { feature: 'tasks', action: 'dragged', kind: 'reorder', status: newStatus, input: 'touch' })
        } else {
          this._moveCardAtPlaceholder(card, targetContainer, newStatus, oldStatus)
          window.phCapture?.('kanban_card_dragged', { feature: 'tasks', action: 'dragged', kind: 'move', from_status: oldStatus, to_status: newStatus, input: 'touch' })
        }
      } else {
        this.dragPlaceholder?.remove()
      }
    } else {
      this.dragPlaceholder?.remove()
    }

    this.dragPlaceholder = null
    this.touchCard = null
    this.touchTargetColumn = null
    this.touchDragStarted = false
    this.draggedTaskId = null
    this.sourceContainer = null
  }

  // ─── Highlight parent card ────────────────────────────────────────────────

  highlightParent(event) {
    event.preventDefault()
    event.stopPropagation()

    const parentId = event.currentTarget.dataset.parentId
    if (!parentId) return

    const parentCard = document.getElementById(`kanban-task-${parentId}`)
    if (!parentCard) return

    // Scroll parent card into view smoothly
    parentCard.scrollIntoView({ behavior: 'smooth', block: 'nearest' })

    // Flash animation: add class, remove after animation completes
    parentCard.classList.add('kanban-card--flash')
    parentCard.addEventListener('animationend', () => {
      parentCard.classList.remove('kanban-card--flash')
    }, { once: true })
  }

  // ─── Reset column order ───────────────────────────────────────────────────

  resetColumnOrder(event) {
    const button = event.currentTarget
    const status = button.dataset.status
    if (!status) return

    fetch(reset_kanban_column_tasks_path(), {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-CSRF-Token': this.csrfTokenValue
      },
      body: JSON.stringify({ status })
    })
      .then(response => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`)
        // Hide the reset button and reorder the column DOM by duedate
        button.closest('.kanban-reset-btn-wrapper')?.remove()
        this._sortColumnByDuedate(status)
      })
      .catch(error => console.error('[task_kanban_controller] reset failed', error))
  }

  // ─── Private helpers ──────────────────────────────────────────────────────

  _insertPlaceholderAt(container, clientY) {
    if (!this.dragPlaceholder) return

    const cards = [...container.querySelectorAll('[data-task-kanban-target="card"]')]
      .filter(c => c !== this.draggedCard)

    let insertBefore = null
    for (const card of cards) {
      const rect = card.getBoundingClientRect()
      if (clientY < rect.top + rect.height / 2) {
        insertBefore = card
        break
      }
    }

    if (insertBefore) {
      container.insertBefore(this.dragPlaceholder, insertBefore)
    } else {
      container.appendChild(this.dragPlaceholder)
    }
  }

  // Reorder a card within its own column (no status change).
  _reorderCardAtPlaceholder(card, container, status) {
    if (this.dragPlaceholder && this.dragPlaceholder.parentNode === container) {
      container.insertBefore(card, this.dragPlaceholder)
      this.dragPlaceholder.remove()
    } else {
      this.dragPlaceholder?.remove()
      container.appendChild(card)
    }
    this.dragPlaceholder = null

    // Show reset button (custom order is now active for this column)
    this._showResetButton(container.closest('[data-task-kanban-target="column"]'), status)

    // Persist the new order
    this._persistColumnOrder(status, container)
  }

  _moveCardAtPlaceholder(card, targetContainer, newStatus, oldStatus) {
    const emptyPh = targetContainer.querySelector('[data-task-kanban-target="emptyPlaceholder"]')
    if (emptyPh) emptyPh.remove()

    if (this.dragPlaceholder && this.dragPlaceholder.parentNode === targetContainer) {
      targetContainer.insertBefore(card, this.dragPlaceholder)
      this.dragPlaceholder.remove()
    } else {
      this.dragPlaceholder?.remove()
      targetContainer.appendChild(card)
    }
    this.dragPlaceholder = null

    if (this.sourceContainer) {
      const remaining = this.sourceContainer.querySelectorAll('[data-task-kanban-target="card"]')
      if (remaining.length === 0) this._addEmptyColumnPlaceholder(this.sourceContainer)
    }

    this._updateColumnCounter(this.sourceContainer)
    this._updateColumnCounter(targetContainer)

    // Show reset button on the target column (custom order is now active)
    this._showResetButton(targetContainer.closest('[data-task-kanban-target="column"]'), newStatus)

    const taskId = this.draggedTaskId || card.dataset.taskId
    this._persistStatusAndOrder(taskId, newStatus, oldStatus, targetContainer, card)
  }

  _persistStatusAndOrder(taskId, newStatus, oldStatus, targetContainer, card) {
    // Step 1: update status
    const statusUrl = update_status_task_path(taskId)
    const statusData = new FormData()
    statusData.append('task[status]', newStatus)

    fetch(statusUrl, {
      method: 'PATCH',
      headers: {
        'Accept': 'text/vnd.turbo-stream.html, text/html, application/xhtml+xml',
        'X-CSRF-Token': this.csrfTokenValue
      },
      body: statusData
    })
      .then(response => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`)
        return response.text()
      })
      .then(html => {
        if (html && html.trim().length > 0) Turbo.renderStreamMessage(html)
        // Step 2: persist new column order
        this._persistColumnOrder(newStatus, targetContainer)
      })
      .catch(error => {
        console.error('[task_kanban_controller] status update failed', error)
        this._revertCardMove(card, oldStatus, targetContainer)
      })
  }

  _persistColumnOrder(status, container) {
    const taskIds = [...container.querySelectorAll('[data-task-kanban-target="card"]')]
      .map(c => parseInt(c.dataset.taskId, 10))
      .filter(Boolean)

    if (taskIds.length === 0) return

    fetch(reorder_kanban_column_tasks_path(), {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-CSRF-Token': this.csrfTokenValue
      },
      body: JSON.stringify({ status, task_ids: taskIds })
    })
      .catch(error => console.error('[task_kanban_controller] reorder failed', error))
  }

  _revertCardMove(card, oldStatus, targetContainer) {
    if (oldStatus) {
      const originalContainer = this.element.querySelector(
        `[data-task-kanban-target="cardsContainer"][data-status="${oldStatus}"]`
      )
      if (originalContainer) {
        const ph = originalContainer.querySelector('[data-task-kanban-target="emptyPlaceholder"]')
        if (ph) ph.remove()
        originalContainer.appendChild(card)
        this._updateColumnCounter(originalContainer)
        this._updateColumnCounter(targetContainer)
      }
    }
  }

  _showResetButton(column, status) {
    if (!column) return
    // Only add if not already present
    if (column.querySelector('.kanban-reset-btn-wrapper')) return
    const header = column.querySelector('.kanban-column-header')
    if (!header) return

    // Insert into the right-side flex container (same as server-rendered structure),
    // before the badge — not appended to the header directly.
    const rightSide = header.querySelector('.d-flex.align-items-center')
    if (!rightSide) return

    const wrapper = document.createElement('span')
    wrapper.className = 'kanban-reset-btn-wrapper'
    const btn = document.createElement('button')
    btn.type = 'button'
    btn.className = 'btn btn-link btn-sm p-0 text-white kanban-reset-btn'
    btn.title = this.element.dataset.resetLabel || 'Rendezés visszaállítása'
    btn.dataset.status = status
    btn.dataset.action = 'click->task-kanban#resetColumnOrder'
    btn.innerHTML = '<i class="bi bi-arrow-counterclockwise"></i>'
    wrapper.appendChild(btn)
    rightSide.insertBefore(wrapper, rightSide.firstChild)
  }

  _sortColumnByDuedate(status) {
    const container = this.element.querySelector(
      `[data-task-kanban-target="cardsContainer"][data-status="${status}"]`
    )
    if (!container) return

    const cards = [...container.querySelectorAll('[data-task-kanban-target="card"]')]
    cards.sort((a, b) => {
      const da = a.dataset.duedate || ''
      const db = b.dataset.duedate || ''
      if (!da && !db) return 0
      if (!da) return 1
      if (!db) return -1
      return da < db ? -1 : da > db ? 1 : 0
    })
    cards.forEach(c => container.appendChild(c))
  }

  _createDropPlaceholder(minHeight = 64) {
    const ph = document.createElement('div')
    ph.className = 'kanban-drop-placeholder'
    ph.style.minHeight = `${minHeight}px`
    return ph
  }

  _startTouchDrag(touch) {
    const card = this.touchCard
    if (!card) return
    this.touchDragStarted = true

    const clone = card.cloneNode(true)
    clone.style.cssText = `
      position: fixed;
      pointer-events: none;
      z-index: 9999;
      width: ${card.offsetWidth}px;
      opacity: 0.85;
      transform: rotate(2deg) scale(1.03);
      box-shadow: 0 8px 24px rgba(0,0,0,0.18);
      transition: none;
    `
    document.body.appendChild(clone)
    this.touchClone = clone
    this.dragPlaceholder = this._createDropPlaceholder(card.offsetHeight)
    this._positionTouchClone(touch)
    card.classList.add('kanban-card--dragging')

    if (navigator.vibrate) navigator.vibrate(40)
  }

  _cancelTouchDrag() {
    clearTimeout(this.touchHoldTimer)
    this.touchHoldTimer = null
    this._removeTouchClone()
    this.dragPlaceholder?.remove()
    this.dragPlaceholder = null
    if (this.touchCard) this.touchCard.classList.remove('kanban-card--dragging')
    if (this.touchTargetColumn) this.touchTargetColumn.classList.remove('kanban-column--drag-over')
    this.touchCard = null
    this.touchTargetColumn = null
    this.touchDragStarted = false
    this.draggedTaskId = null
    this.sourceContainer = null
  }

  _positionTouchClone(touch) {
    if (!this.touchClone) return
    this.touchClone.style.left = `${touch.clientX - this.touchClone.offsetWidth / 2}px`
    this.touchClone.style.top = `${touch.clientY - 30}px`
  }

  _removeTouchClone() {
    if (this.touchClone) {
      this.touchClone.remove()
      this.touchClone = null
    }
  }

  _clearAllDragOver() {
    this.columnTargets.forEach(c => c.classList.remove('kanban-column--drag-over'))
  }

  _addEmptyColumnPlaceholder(container) {
    const ph = document.createElement('div')
    ph.className = 'kanban-empty-column text-center text-muted py-3 small'
    ph.setAttribute('data-task-kanban-target', 'emptyPlaceholder')
    ph.innerHTML = '<i class="bi bi-inbox"></i><div></div>'
    container.appendChild(ph)
  }

  _updateColumnCounter(container) {
    if (!container) return
    const column = container.closest('[data-task-kanban-target="column"]')
    if (!column) return
    const badge = column.querySelector('.kanban-column-header .badge')
    if (!badge) return
    badge.textContent = container.querySelectorAll('[data-task-kanban-target="card"]').length
  }
}
